import { useEffect, useRef, useState, useCallback, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import {
  FolderKanban, FileText, ShieldCheck, AlertTriangle, Building2, Camera, MessageSquarePlus,
  PenLine, Download, CheckCircle2, Image as ImageIcon, Loader2, Smartphone, Eye,
} from 'lucide-react';
import type { ProjectRol, DocumentCategorie } from '../hooks/useProjecten.js';
import { useInstallPrompt } from '../../../core/shared/hooks/useInstallPrompt.js';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';
import { SignatureCanvas, getSignatureRef } from '../../buitendienst/components/SignatureCanvas.js';

// Publieke magic-link werf-app voor externe rollen (klant / projectleider / plaatser / leverdienst).
// Lezen: planning + documenten + feedback + handtekeningen (rol-gescoped, geen prijzen/marges).
// Schrijven: foto's uploaden (werf-rollen), feedback plaatsen (iedereen), digitaal ondertekenen (werf + klant).

interface FactuurGoedkeurder { naam: string; rol: ProjectRol; opmerking: string | null; goedgekeurdOp: string }
interface FactuurGoedkeuring { lijst: FactuurGoedkeurder[]; volledig: boolean; ikGoedgekeurd: boolean }
interface PortaalDoc {
  id: string;
  titel: string;
  categorie: DocumentCategorie;
  versie: number;
  bestandsgrootte: number;
  mimeType: string;
  createdAt: string;
  downloadUrl: string | null;
  unitCode: string | null;
  geuploadDoorNaam: string | null;
  goedkeuring: FactuurGoedkeuring | null;
}
interface PortaalUnit {
  code: string;
  status: string;
  keukenType: { code: string } | null;
  bestelbonStatus: 'GEEN' | 'OPGEMAAKT' | 'ONTVANGEN' | 'BEVESTIGD';
  leverdatum: string | null;
  plaatsingsdatum: string | null;
  opleveringsdatum: string | null;
  schadeOpen: boolean;
}
interface PortaalComment {
  id: string;
  tekst: string;
  auteurNaam: string | null;
  auteurRol: ProjectRol;
  createdAt: string;
  unitCode: string | null;
  vanMij: boolean;
}
interface PortaalHandtekening {
  id: string;
  ondertekenaarNaam: string;
  ondertekenaarRol: ProjectRol;
  verklaring: string | null;
  ondertekendOp: string;
  documentId: string | null;
  unitCode: string | null;
}
interface PortaalBranding { primairHex: string; secundairHex: string; naam: string; isDefault: boolean; logoUrl: string | null }
interface PortaalData {
  rol: ProjectRol;
  genodigde: { naam: string | null; email: string | null };
  preview?: boolean;
  branding: PortaalBranding;
  rechten: { magUploaden: boolean; magOndertekenen: boolean; magFeedback: boolean; magFacturenGoedkeuren: boolean };
  project: { id: string; entityId: string; naam: string; klantNaam: string | null; adres: string | null; aantalUnits: number; status: string };
  documenten: PortaalDoc[];
  units: PortaalUnit[];
  opmerkingen: PortaalComment[];
  handtekeningen: PortaalHandtekening[];
}

function pDatum(s: string | null): string {
  return s ? new Date(s).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit' }) : '—';
}
function pDatumTijd(s: string): string {
  return new Date(s).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
const BESTELBON_TXT: Record<string, string> = { BEVESTIGD: 'bevestigd', ONTVANGEN: 'ontvangen', OPGEMAAKT: 'opgemaakt', GEEN: '—' };

const CATEGORIE_LABEL: Record<DocumentCategorie, string> = {
  BOUWPLAN: 'Bouwplannen',
  OFFERTE: 'Offertes',
  RENDER: 'Renders',
  CONTRACT: 'Contracten',
  CORRESPONDENTIE: 'Correspondentie',
  JURIDISCH: 'Juridisch',
  FINANCIEEL: 'Financieel',
  FACTUUR: 'Facturen',
  OPLEVERING: 'Oplevering & nazorg',
  WERFFOTO: 'Werffoto\'s',
  ALGEMEEN: 'Algemeen',
};
const ROL_LABEL: Record<ProjectRol, string> = {
  VERANTWOORDELIJKE: 'Verantwoordelijke',
  VERKOPER: 'Verkoper',
  FRANCHISENEMER: 'Franchisenemer',
  KLANT: 'Klant',
  PROJECTLEIDER: 'Projectleider',
  EINDKLANT: 'Eindklant',
  PLAATSER: 'Plaatser',
  LEVERDIENST: 'Leverdienst',
};

export function ProjectPortaalPage({ preview = false }: { preview?: boolean } = {}) {
  const { token, id, lidId } = useParams<{ token?: string; id?: string; lidId?: string }>();
  const [data, setData] = useState<PortaalData | null>(null);
  const [status, setStatus] = useState<'laden' | 'ok' | 'ongeldig' | 'fout'>('laden');
  const { canInstall, isInstalled, install } = useInstallPrompt();

  const laad = useCallback(async () => {
    try {
      if (preview) {
        // Interne read-only "Bekijk als": authed endpoint, geen magic-link.
        if (!id || !lidId) { setStatus('fout'); return; }
        const d = await apiFetch<PortaalData>(`/api/projecten/${id}/leden/${lidId}/portaal-preview`);
        setData(d);
        setStatus('ok');
        return;
      }
      if (!token) return;
      const r = await fetch(`/api/public/projecten-portaal/${token}`);
      if (r.status === 404) { setStatus('ongeldig'); return; }
      if (!r.ok) throw new Error('fout');
      setData(await r.json());
      setStatus('ok');
    } catch {
      setStatus('fout');
    }
  }, [token, preview, id, lidId]);

  useEffect(() => { laad(); }, [laad]);

  // PWA: wijs het manifest + apple-touch-icon naar dit projectportaal, zodat "Zet op beginscherm"
  // een app installeert die rechtstreeks dit project opent (met winkel-branding). Niet in preview.
  useEffect(() => {
    if (preview || status !== 'ok' || !data || !token) return;
    const head = document.head;
    const setLink = (rel: string, href: string, extra?: Record<string, string>) => {
      let el = head.querySelector<HTMLLinkElement>(`link[data-portaal="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('data-portaal', rel); head.appendChild(el); }
      el.rel = rel; el.href = href;
      if (extra) for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
    };
    setLink('manifest', `/api/public/projecten-portaal/${token}/manifest.webmanifest`);
    setLink('apple-touch-icon', `/api/public/pwa/icon/${data.project.entityId}/192.png`);
    let title = head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"][data-portaal="1"]');
    if (!title) { title = document.createElement('meta'); title.name = 'apple-mobile-web-app-title'; title.setAttribute('data-portaal', '1'); head.appendChild(title); }
    title.content = data.project.naam.slice(0, 15);
    return () => {
      head.querySelectorAll('[data-portaal]').forEach((el) => el.remove());
    };
  }, [status, data, token]);

  // Foto-raster = afbeeldingen uit de werf-/nazorg-categorieën (werffoto's + opleverings-/schadefoto's).
  // Zo staan foto's nooit meer tussen de PDF's (bv. DNV-rapporten), maar blijven bouwplannen/renders/
  // contracten gewoon bij de documenten — ook als die toevallig een .png/.jpg zijn.
  const FOTO_CATEGORIEEN: DocumentCategorie[] = ['WERFFOTO', 'OPLEVERING'];
  const isAfb = (d: PortaalDoc) => !!d.mimeType?.startsWith('image/');
  const isWerffoto = (d: PortaalDoc) => isAfb(d) && FOTO_CATEGORIEEN.includes(d.categorie);
  const isRender = (d: PortaalDoc) => isAfb(d) && d.categorie === 'RENDER';
  const fotos = (data?.documenten ?? []).filter(isWerffoto);
  const renders = (data?.documenten ?? []).filter(isRender);
  const overigeDocs = (data?.documenten ?? []).filter((d) => !isWerffoto(d) && !isRender(d));
  const perCategorie = overigeDocs.reduce<Record<string, PortaalDoc[]>>((acc, d) => {
    (acc[d.categorie] ??= []).push(d);
    return acc;
  }, {});
  const accent = data?.branding?.primairHex ?? '#C8A961';

  const merkNaam = data?.branding && !data.branding.isDefault ? data.branding.naam : 'Kordaro';

  return (
    <div className="min-h-screen bg-background" style={{ '--brand': accent } as CSSProperties}>
      {data?.preview && (
        <div className="bg-amber-500 text-white text-sm px-5 py-2 flex items-center gap-2 sticky top-0 z-20">
          <Eye size={15} className="shrink-0" />
          <span className="flex-1">Voorbeeld — zo ziet <strong>{data.genodigde.naam || 'dit contact'}</strong> ({ROL_LABEL[data.rol]}) het portaal. Acties zijn uitgeschakeld.</span>
          <button onClick={() => window.close()} className="underline shrink-0 hover:opacity-90">Sluiten</button>
        </div>
      )}
      <header className="border-b border-card-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {data?.branding?.logoUrl ? (
              <img
                src={data.branding.logoUrl}
                alt={merkNaam}
                className="h-8 w-8 rounded-lg object-contain bg-white shrink-0"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-8 h-8 rounded-lg text-white grid place-items-center shrink-0" style={{ backgroundColor: accent }}>
                <FolderKanban size={18} />
              </div>
            )}
            <span className="font-semibold text-foreground truncate">{merkNaam} · Projectportaal</span>
          </div>
          {canInstall && !isInstalled && (
            <button
              onClick={() => install()}
              className="inline-flex items-center gap-1.5 rounded-lg text-white px-3 py-1.5 text-xs font-medium hover:opacity-90 shrink-0"
              style={{ backgroundColor: accent }}
            >
              <Smartphone size={14} /> App installeren
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6">
        {status === 'laden' && <p className="text-sm text-muted-foreground">Laden…</p>}

        {status === 'ongeldig' && (
          <div className="text-center py-16">
            <AlertTriangle size={40} className="mx-auto text-amber-500 mb-4" />
            <h1 className="text-lg font-semibold text-foreground">Ongeldige of verlopen link</h1>
            <p className="text-sm text-muted-foreground mt-1">Vraag een nieuwe uitnodiging aan je contactpersoon.</p>
          </div>
        )}
        {status === 'fout' && (
          <div className="text-center py-16">
            <AlertTriangle size={40} className="mx-auto text-red-500 mb-4" />
            <h1 className="text-lg font-semibold text-foreground">Er ging iets mis</h1>
            <p className="text-sm text-muted-foreground mt-1">Probeer de link later opnieuw te openen.</p>
          </div>
        )}

        {status === 'ok' && data && (
          <div className="space-y-6">
            {/* Projectkop */}
            <div>
              {data.genodigde.naam && <p className="text-sm text-muted-foreground">Welkom, {data.genodigde.naam}</p>}
              <h1 className="text-2xl font-semibold text-foreground">{data.project.naam}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                {data.project.klantNaam && <span className="inline-flex items-center gap-1"><Building2 size={14} /> {data.project.klantNaam}</span>}
                {data.project.adres && <span>· {data.project.adres}</span>}
                <span>· {data.project.aantalUnits} keukens</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{data.project.status}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
                <ShieldCheck size={12} /> Je bekijkt dit als <strong className="text-foreground">{ROL_LABEL[data.rol]}</strong>.
              </p>
            </div>

            {/* Install-hint op iOS (geen beforeinstallprompt) */}
            {!isInstalled && !canInstall && (
              <div className="rounded-[14px] border border-card-border bg-card px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
                <Smartphone size={15} className="shrink-0 mt-0.5" />
                <span>Zet dit project als app op je gsm: tik op <strong>Deel</strong> → <strong>Zet op beginscherm</strong> (iPhone), of via het browsermenu op Android.</span>
              </div>
            )}

            {/* Planning per keuken */}
            {data.units.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-foreground mb-2">Planning per keuken</h2>
                <div className="border border-card-border rounded-[14px] overflow-hidden bg-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground bg-muted/40">
                          <th className="px-3 py-2 font-medium">Keuken</th>
                          <th className="px-2 py-2 font-medium">Bestelbon</th>
                          <th className="px-2 py-2 font-medium">Levering</th>
                          <th className="px-2 py-2 font-medium">Plaatsing</th>
                          <th className="px-2 py-2 font-medium">Oplevering</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.units.map((u) => (
                          <tr key={u.code} className="border-t border-card-border">
                            <td className="px-3 py-2 font-medium text-foreground">{u.code}</td>
                            <td className="px-2 py-2 text-muted-foreground">{BESTELBON_TXT[u.bestelbonStatus]}</td>
                            <td className="px-2 py-2 text-muted-foreground">{pDatum(u.leverdatum)}</td>
                            <td className="px-2 py-2 text-muted-foreground">{pDatum(u.plaatsingsdatum)}</td>
                            <td className="px-2 py-2">
                              {u.schadeOpen ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle size={12} /> schade</span>
                              ) : u.opleveringsdatum ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">opgeleverd</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Werf-acties: foto uploaden (werf-rollen) */}
            {data.rechten.magUploaden && (
              <FotoUpload token={token!} units={data.units.map((u) => u.code)} onDone={laad} />
            )}

            {/* Foto's van de werf — alle afbeeldingen als thumbnails (los van de documenten) */}
            {fotos.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-foreground mb-2 inline-flex items-center gap-1.5"><ImageIcon size={15} /> Foto's van de werf ({fotos.length})</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {fotos.map((f) => (
                    <a key={f.id} href={f.downloadUrl ?? '#'} target="_blank" rel="noopener noreferrer" title={`${f.titel}${f.unitCode ? ` · ${f.unitCode}` : ''}`} className="group block">
                      <div className="aspect-square rounded-lg overflow-hidden border border-card-border bg-muted">
                        {f.downloadUrl
                          ? <img src={f.downloadUrl} alt={f.titel} loading="lazy" className="w-full h-full object-cover group-hover:opacity-90" />
                          : <div className="w-full h-full grid place-items-center text-muted-foreground"><ImageIcon size={18} /></div>}
                      </div>
                      {f.unitCode && <p className="mt-1 text-[11px] text-muted-foreground truncate">{f.unitCode}</p>}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Renders — 3D-beelden als thumbnails (apart van de werffoto's) */}
            {renders.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-foreground mb-2 inline-flex items-center gap-1.5"><ImageIcon size={15} /> Renders ({renders.length})</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {renders.map((f) => (
                    <a key={f.id} href={f.downloadUrl ?? '#'} target="_blank" rel="noopener noreferrer" title={`${f.titel}${f.unitCode ? ` · ${f.unitCode}` : ''}`} className="group block">
                      <div className="aspect-[4/3] rounded-lg overflow-hidden border border-card-border bg-muted">
                        {f.downloadUrl
                          ? <img src={f.downloadUrl} alt={f.titel} loading="lazy" className="w-full h-full object-cover group-hover:opacity-90" />
                          : <div className="w-full h-full grid place-items-center text-muted-foreground"><ImageIcon size={18} /></div>}
                      </div>
                      {f.unitCode && <p className="mt-1 text-[11px] text-muted-foreground truncate">{f.unitCode}</p>}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Documenten per categorie */}
            {Object.keys(perCategorie).length > 0 && (
              <div className="space-y-4">
                {Object.entries(perCategorie).map(([cat, docs]) => (
                  <div key={cat} className="border border-card-border rounded-[14px] p-5 bg-card">
                    <h2 className="text-sm font-medium text-foreground mb-3">{CATEGORIE_LABEL[cat as DocumentCategorie]} ({docs.length})</h2>
                    <ul className="divide-y divide-card-border">
                      {docs.map((d) => (
                        <li key={d.id} className="py-2">
                          {d.downloadUrl ? (
                            <a href={d.downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                              <FileText size={16} className="text-muted-foreground shrink-0" />
                              <span className="text-sm text-foreground truncate flex-1 group-hover:underline">
                                {d.titel} {d.unitCode && <span className="text-muted-foreground">· {d.unitCode}</span>} {d.versie > 1 && <span className="text-muted-foreground">v{d.versie}</span>}
                              </span>
                              <Download size={14} className="text-muted-foreground shrink-0" />
                            </a>
                          ) : (
                            <div className="flex items-center gap-3">
                              <FileText size={16} className="text-muted-foreground shrink-0" />
                              <span className="text-sm text-muted-foreground truncate flex-1">{d.titel}</span>
                            </div>
                          )}
                          {d.categorie === 'FACTUUR' && d.goedkeuring && (
                            <FactuurAkkoord doc={d} token={token!} magGoedkeuren={data.rechten.magFacturenGoedkeuren} accent={accent} onDone={laad} />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {!data.documenten.length && (
              <div className="border border-card-border rounded-[14px] p-8 text-center">
                <p className="text-sm text-muted-foreground">Er zijn nog geen documenten voor je gedeeld.</p>
              </div>
            )}

            {/* Ondertekenen (werf + klant) */}
            {data.rechten.magOndertekenen && (
              <Ondertekenen
                token={token!}
                rolNaam={data.genodigde.naam ?? ''}
                units={data.units.map((u) => u.code)}
                docs={data.documenten.filter((d) => d.categorie === 'OPLEVERING' || d.categorie === 'CONTRACT' || d.categorie === 'JURIDISCH')}
                onDone={laad}
              />
            )}

            {/* Reeds gezette handtekeningen */}
            {data.handtekeningen.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-foreground mb-2">Ondertekeningen ({data.handtekeningen.length})</h2>
                <ul className="border border-card-border rounded-[14px] bg-card divide-y divide-card-border">
                  {data.handtekeningen.map((h) => (
                    <li key={h.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                      <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="text-foreground font-medium">{h.ondertekenaarNaam}</span>
                        <span className="text-muted-foreground"> · {ROL_LABEL[h.ondertekenaarRol]}{h.unitCode ? ` · ${h.unitCode}` : ''}</span>
                        {h.verklaring && <p className="text-xs text-muted-foreground truncate">{h.verklaring}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{pDatumTijd(h.ondertekendOp)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Feedback */}
            <Feedback
              token={token ?? ''}
              rol={data.rol}
              canPost={data.rechten.magFeedback}
              units={data.units.map((u) => u.code)}
              opmerkingen={data.opmerkingen}
              onDone={laad}
            />
          </div>
        )}
      </main>
    </div>
  );
}

// ─── Factuur-goedkeuring (werfleider + facturen-ontvanger) ───────────────────
function FactuurAkkoord({ doc, token, magGoedkeuren, accent, onDone }: { doc: PortaalDoc; token: string; magGoedkeuren: boolean; accent: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const gk = doc.goedkeuring!;

  async function keurGoed() {
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/facturen/${doc.id}/goedkeuren`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Goedkeuren mislukt');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Goedkeuren mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 ml-7 flex flex-wrap items-center gap-x-3 gap-y-1">
      {gk.volledig ? (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700"><CheckCircle2 size={12} /> Volledig goedgekeurd</span>
      ) : (
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Wacht op goedkeuring</span>
      )}
      {gk.lijst.map((g, i) => (
        <span key={i} className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5"><CheckCircle2 size={10} className="text-green-600" /> {g.naam} · {ROL_LABEL[g.rol]}</span>
      ))}
      {magGoedkeuren && !gk.ikGoedgekeurd && (
        <button onClick={keurGoed} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg text-white px-3 py-1 text-xs font-medium hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: accent }}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Goedkeuren
        </button>
      )}
      {magGoedkeuren && gk.ikGoedgekeurd && <span className="text-[11px] text-green-700 font-medium">Jij hebt goedgekeurd</span>}
      {fout && <span className="text-[11px] text-red-600">{fout}</span>}
    </div>
  );
}

// ─── Foto-upload (werf-rollen) ───────────────────────────────────────────────
function FotoUpload({ token, units, onDone }: { token: string; units: string[]; onDone: () => void }) {
  const [unitCode, setUnitCode] = useState('');
  const [titel, setTitel] = useState('');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setBusy(true); setFout('');
    try {
      const fd = new FormData();
      fd.append('foto', file);
      if (unitCode) fd.append('unitCode', unitCode);
      if (titel.trim()) fd.append('titel', titel.trim());
      const r = await fetch(`/api/public/projecten-portaal/${token}/fotos`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Upload mislukt');
      setTitel('');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Upload mislukt');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="border border-card-border rounded-[14px] p-4 bg-card">
      <h2 className="text-sm font-medium text-foreground mb-3 inline-flex items-center gap-1.5"><Camera size={15} /> Foto van de werf toevoegen</h2>
      <div className="flex flex-col sm:flex-row gap-2">
        {units.length > 0 && (
          <select value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm">
            <option value="">Hele project</option>
            {units.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Omschrijving (optioneel)" className="flex-1 rounded-lg border border-card-border bg-background px-3 py-2 text-sm" />
      </div>
      {/* Géén capture-attribuut → iOS/Android tonen zowel "Foto maken" als "Kies uit bibliotheek". */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: 'var(--brand, #C8A961)' }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {busy ? 'Uploaden…' : 'Foto maken of kiezen'}
      </button>
      {fout && <p className="text-xs text-red-600 mt-2">{fout}</p>}
    </div>
  );
}

// ─── Feedback ────────────────────────────────────────────────────────────────
function Feedback({ token, rol, canPost, units, opmerkingen, onDone }: { token: string; rol: ProjectRol; canPost: boolean; units: string[]; opmerkingen: PortaalComment[]; onDone: () => void }) {
  const [tekst, setTekst] = useState('');
  const [unitCode, setUnitCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');

  async function plaats() {
    if (tekst.trim().length < 1) return;
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/opmerkingen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: tekst.trim(), unitCode: unitCode || undefined }),
      });
      if (!r.ok) throw new Error('Plaatsen mislukt');
      setTekst(''); setUnitCode('');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Plaatsen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-card-border rounded-[14px] p-4 bg-card">
      <h2 className="text-sm font-medium text-foreground mb-3 inline-flex items-center gap-1.5"><MessageSquarePlus size={15} /> Feedback & opmerkingen</h2>
      {canPost && (
      <div className="flex flex-col gap-2">
        {units.length > 0 && (
          <select value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm">
            <option value="">Hele project</option>
            {units.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <textarea
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          placeholder={rol === 'KLANT' || rol === 'EINDKLANT' ? 'Een vraag of opmerking over je keuken…' : 'Een opmerking vanaf de werf…'}
          rows={3}
          className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm resize-y"
        />
        <button onClick={plaats} disabled={busy || !tekst.trim()} className="self-end inline-flex items-center gap-2 rounded-lg text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: 'var(--brand, #C8A961)' }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <MessageSquarePlus size={15} />} Plaatsen
        </button>
        {fout && <p className="text-xs text-red-600">{fout}</p>}
      </div>
      )}
      {!canPost && !opmerkingen.length && <p className="text-sm text-muted-foreground">Nog geen opmerkingen.</p>}

      {opmerkingen.length > 0 && (
        <ul className="mt-4 space-y-2">
          {opmerkingen.map((o) => (
            <li key={o.id} className={`rounded-lg px-3 py-2 text-sm ${o.vanMij ? 'bg-foreground/5 border border-card-border' : 'bg-muted/40'}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{o.auteurNaam ?? 'Onbekend'}</span>
                <span>· {ROL_LABEL[o.auteurRol]}</span>
                {o.unitCode && <span>· {o.unitCode}</span>}
                <span className="ml-auto">{pDatumTijd(o.createdAt)}</span>
              </div>
              <p className="text-foreground mt-1 whitespace-pre-wrap">{o.tekst}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Ondertekenen (werf + klant) ─────────────────────────────────────────────
function Ondertekenen({ token, rolNaam, units, docs, onDone }: { token: string; rolNaam: string; units: string[]; docs: PortaalDoc[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [unitCode, setUnitCode] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [verklaring, setVerklaring] = useState('Gelezen en goedgekeurd — oplevering');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const [hasContent, setHasContent] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const setCanvasEl = useCallback((el: HTMLDivElement | null) => {
    if (el) canvasRef.current = el.querySelector('canvas');
  }, []);

  async function teken() {
    const ref = getSignatureRef(canvasRef.current);
    if (!ref || ref.isEmpty()) { setFout('Zet eerst je handtekening.'); return; }
    if (!ref.isMinimumSize()) { setFout('De handtekening is te klein.'); return; }
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/ondertekeningen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handtekeningData: ref.toDataURL('image/png', 1.0),
          unitCode: unitCode || undefined,
          documentId: documentId || undefined,
          verklaring: verklaring.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Ondertekenen mislukt');
      setOpen(false); ref.clear();
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Ondertekenen mislukt');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-[14px] border border-card-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:border-foreground/30">
        <PenLine size={16} /> Digitaal ondertekenen
      </button>
    );
  }

  return (
    <div className="border border-card-border rounded-[14px] p-4 bg-card">
      <h2 className="text-sm font-medium text-foreground mb-3 inline-flex items-center gap-1.5"><PenLine size={15} /> Digitaal ondertekenen</h2>
      <div className="flex flex-col gap-2 mb-3">
        {units.length > 0 && (
          <select value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm">
            <option value="">Hele project</option>
            {units.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {docs.length > 0 && (
          <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm">
            <option value="">Geen specifiek document</option>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.titel}</option>)}
          </select>
        )}
        <input value={verklaring} onChange={(e) => setVerklaring(e.target.value)} placeholder="Verklaring" className="rounded-lg border border-card-border bg-background px-3 py-2 text-sm" />
      </div>
      <div ref={setCanvasEl}>
        <SignatureCanvas height={200} onChange={setHasContent} initialName={rolNaam} showNameDate={false} />
      </div>
      {fout && <p className="text-xs text-red-600 mt-2">{fout}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={() => { setOpen(false); setFout(''); }} className="flex-1 rounded-lg border border-card-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Annuleren</button>
        <button onClick={teken} disabled={busy || !hasContent} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: 'var(--brand, #C8A961)' }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Ondertekenen
        </button>
      </div>
    </div>
  );
}
